import {
  BaseItem,
  CanHaveAnimation,
  CanHaveRotation,
  RenderMetaBaseItemCore,
} from '../shared';
import {
  FontStyle,
  TextAlign,
  TextDirection,
  TextShadow,
  TextItemBackground,
} from '../text/text-item-type';
import {
  EditorItemType,
  EditorStarterItem,
} from '@/features/remotion-editor/editor/items/item-type';
import { ClipType } from '@/shared/apis/editor/clip-types';
import type { CaptionHighlightEffect } from '@/shared/lib/captions/caption-highlight-effect';
import type { WakaSourceTypography } from '@waka/compat/source-caption-presentation';

export type CaptionsItemRenderMeta = {
  clipType: Extract<ClipType, 'caption'>;
  sceneId: number;
} & RenderMetaBaseItemCore;

export type CaptionsItem = BaseItem &
  CanHaveAnimation &
  CanHaveRotation & {
    type: Extract<EditorItemType, 'captions'>;
    sourceTypography?: WakaSourceTypography;
    /** Explicit user edge extension holds the boundary page; word timestamps remain original. */
    captionEdgeHold?: { left?: boolean; right?: boolean };
    assetId: string;
    fontFamily: string;
    fontStyle: FontStyle;
    lineHeight: number;
    letterSpacing: number;
    fontSize: number;
    align: TextAlign;
    color: string;
    colorVisible?: boolean;
    highlightColor?: string | null;
    highlightVisible?: boolean;
    highlightMode?: 'cue' | 'word';
    highlightEffect?: CaptionHighlightEffect;
    strokeWidth: number;
    strokeColor: string;
    strokeVisible?: boolean;
    direction: TextDirection;
    pageDurationInMilliseconds: number;
    captionStartInSeconds: number;
    captionSyncLeadMs?: number;
    /** 검증된 forced-alignment word end를 legacy STT 보정 없이 그대로 렌더한다. */
    captionTimingPolicyVersion?: number;
    /**
     * 다음 자막 item과 화면 표시만 하나의 묶음으로 연결한다.
     * sceneId/linkId와 asset은 각각 유지하며, 메인 자막 트랙에서 실제로 인접한 경우에만 적용한다.
     */
    captionPresentationNextItemId?: string;
    /**
     * 사용자가 자막 경계나 표시 시간을 명시적으로 편집한 상태.
     * 저장 재진입 시 미리보기 기반 레거시 보정보다 이 상태를 우선한다.
     */
    hasUserDefinedPresentation?: boolean;
    maxLines: number;
    fadeInDurationInSeconds: number;
    fadeOutDurationInSeconds: number;
    shadow?: TextShadow;
    shadowVisible?: boolean;
    background?: TextItemBackground | null;
    backgroundVisible?: boolean;
    renderMeta?: CaptionsItemRenderMeta;
  };

export const isCaptionsItem = (
  item?: EditorStarterItem,
): item is CaptionsItem => {
  if (!item) return false;

  return item.type === 'captions';
};
